/**
 * Card component for displaying a chat agent in the grid.
 * Matches the design from the screenshots: icon, name, description, timestamp, avatars.
 */

import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ROUTES } from '@/constants/routes';
import type { ChatAgent } from '@/types/chat-agent-types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { getAgentIcon } from './icon-picker';
import { getAgentColorBg } from './agent-color-picker';

interface AgentCardProps {
  agent: ChatAgent;
  onDelete: (agent: ChatAgent) => void;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `Edited ${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Edited ${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `Edited ${diffDays}d ago`;
}

export function AgentCard({ agent, onDelete }: AgentCardProps) {
  const navigate = useOrgNavigate();

  const handleClick = () => {
    navigate(ROUTES.CHAT_AGENTS.CHAT.replace(':agentId', agent.id));
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(ROUTES.CHAT_AGENTS.CONFIG.replace(':agentId', agent.id));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(agent);
  };

  return (
    <div
      onClick={handleClick}
      className='group relative flex flex-col gap-3 rounded-lg border border-border bg-card p-4 cursor-pointer transition-all hover:border-primary/50 hover:shadow-md'
    >
      {/* Icon */}
      <div className='flex items-center justify-between'>
        {(() => {
          const Icon = getAgentIcon(agent.icon);
          const bg = getAgentColorBg(agent.color);
          return (
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${!bg ? 'bg-muted' : ''}`}
              style={bg ? { backgroundColor: bg } : undefined}
            >
              <Icon
                className={`h-5 w-5 ${!agent.color ? 'text-muted-foreground' : ''}`}
                style={agent.color ? { color: agent.color } : undefined}
              />
            </div>
          );
        })()}
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant='ghost'
              size='icon'
              className='size-8 opacity-0 group-hover:opacity-100 transition-opacity'
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className='size-4 mr-2' />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDelete}>
              <Trash2 className='size-4 mr-2' />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Name & Description */}
      <div>
        <h3 className='font-semibold text-sm text-foreground line-clamp-1'>
          {agent.name}
        </h3>
        <p className='text-xs text-muted-foreground mt-1 line-clamp-2'>
          {agent.description || 'No description'}
        </p>
      </div>

      {/* Footer: timestamp */}
      <div className='flex items-center justify-between mt-auto'>
        <span className='text-xs text-muted-foreground'>
          {timeAgo(agent.updated_at)}
        </span>
      </div>
    </div>
  );
}
